import type { AirtableFetchData } from '@ben/columbia/hooks/use-mutation-airtable-fetch';
import type { IModularBenefitMessages } from '@ben/shared/types/columbia';
import { camelCase, mapValues } from 'lodash';
import { useMemo, useState } from 'react';

export interface AirtableState {
  successes: IModularBenefitMessages;
  warnings: IModularBenefitMessages;
}

// Stores AirtableFetchData in state and returns objects
// with relevant success messages and warnings messages
// to be passed in to benefit config form components
export const useAirtableState = (): [
  AirtableState,
  (data?: AirtableFetchData) => void,
] => {
  const [airtableData, setAirtableData] = useState<AirtableFetchData>();

  const successes: IModularBenefitMessages = useMemo(() => {
    const addSuccessMessages = (data?: object) =>
      mapValues(data, (value) =>
        value === null ? undefined : 'Imported from Airtable'
      );
    return {
      ...addSuccessMessages(airtableData?.benefitData),
      benefitWindows: airtableData?.benefitData.benefitWindows?.map(
        (benefitWindow) => addSuccessMessages(benefitWindow)
      ),
    };
  }, [airtableData]);

  const warnings: IModularBenefitMessages = useMemo(() => {
    if (!airtableData) return {};
    return Object.fromEntries(
      airtableData?.importErrors.map((key) => [
        camelCase(key),
        'Unsuccessful import',
      ])
    );
  }, [airtableData]);

  return [
    {
      successes,
      warnings,
    },
    setAirtableData,
  ];
};
